/** Embalados por dia nos últimos 30 dias (dataCheckout do Tiny). Dia sem expediente fica apagado; clicar abre a lista. */
import { useState } from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import type { AbrirLista, Canal, PontoSerie } from './types'
import { COR_SITUACAO, diaMes } from './logic'
import { carregarEmbalados } from './fonte'

type DotProps = { cx?: number; cy?: number; index?: number; payload?: { util: boolean } }

/** Só o filtro de marketplace vale aqui (os outros são de pedidos ativos, não do histórico). */
export function Serie30({ serie, canais, abrirLista }: { serie: PontoSerie[]; canais: Set<Canal>; abrirLista: AbrirLista }) {
  const theme = useTheme()
  const [erro, setErro] = useState<string | null>(null)
  const dados = serie.map((p) => ({
    dia: p.dia, rotulo: diaMes(p.dia), util: p.util,
    total: canais.size ? [...canais].reduce((a, c) => a + (p.por_canal[c] ?? 0), 0) : p.total,
  }))

  const abrir = async (i: number) => {
    const p = dados[i]
    if (!p) return
    setErro(null)
    try {
      const ps = await carregarEmbalados(p.dia)
      abrirLista(`Embalados em ${p.rotulo}`, canais.size ? ps.filter((x) => canais.has(x.canal)) : ps)
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e))
    }
  }

  // o clique é tratado no gráfico inteiro (dia sob o cursor), não no ponto, para não abrir duas vezes
  const ponto = ({ cx, cy, index, payload }: DotProps) => (
    <circle key={`p${index}`} cx={cx} cy={cy} r={payload?.util ? 5 : 4} style={{ cursor: 'pointer' }}
      fill={payload?.util ? COR_SITUACAO[3] : theme.palette.grey[300]} stroke="#fff" strokeWidth={1.5} />
  )

  return (
    <>
      <Box sx={{ height: 260 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={dados} margin={{ top: 12, right: 16, left: 0, bottom: 0 }} style={{ cursor: 'pointer' }}
            onClick={(e) => { const i = Number(e?.activeTooltipIndex); if (Number.isInteger(i)) void abrir(i) }}>
            <CartesianGrid stroke={theme.palette.divider} vertical={false} />
            <XAxis dataKey="rotulo" tick={{ fontSize: 11 }} interval={2} />
            <YAxis tick={{ fontSize: 11 }} width={44} allowDecimals={false} />
            <Tooltip formatter={(v) => [String(v), 'Embalados']}
              labelFormatter={(l, ps) => `${String(l)}${ps?.[0]?.payload?.util === false ? ' · sem expediente' : ''}`} />
            <Line dataKey="total" name="Embalados" stroke={COR_SITUACAO[3]} strokeWidth={2.5} dot={ponto} activeDot={{ r: 7 }} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </Box>
      {erro && <Typography variant="caption" color="error">Não foi possível abrir a lista do dia: {erro}</Typography>}
    </>
  )
}
